import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsError, ToastOptionsSuccess } from "@modernary/mui/components/toasts";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import type { Organization, OrganizationNew, OrganizationQuery } from "./organization";
import organizationService from "./organizationService";
import { LE } from "@/constants/enums";

const URL = "organizations";
const ENTITY_NAME = "Organization";

const axiosParams = {
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Organization>(apiHosts.AQMS, axiosParams);

const getOrganizations = (params: OrganizationQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

type OrganizationIds = {
  id?: string | number;
};
const getNewOrganization = async (ids: OrganizationIds) => {
  const defaultApp = {
    data: {
      ...organizationService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<OrganizationNew, any>;

  return defaultApp;
};

const getOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const createOrganization = (organization: Organization, config: PagingApiAndNotifierConfig) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.post(URL, organization, {
    successToastCb,
    errorToastCb,
  });
};

const updateOrganization = (organization: Organization, config: PagingApiAndNotifierConfig) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.put(`${URL}/${organization.id}`, organization, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {Organization} organization
 */
const saveOrganization = (organization: Organization, config = {}) => {
  if (organization.id) {
    return updateOrganization(organization, config);
  } else {
    return createOrganization(organization, config);
  }
};

const deleteOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successToastCb,
    errorToastCb,
  });
};

export {
  getOrganizations,
  getNewOrganization,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  saveOrganization,
  deleteOrganizationById,
};
